import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './Button';
import { Input } from './Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './Dialog';
import { Plus, PencilSimple, Trash, ArrowsClockwise } from 'phosphor-react';
import { useApi } from '../hooks/useApi';
import { Vendedor } from '../services/api';
import { Loader } from './Loader';

export const VendedoresSection: React.FC<{ onSaved?: () => void }> = ({ onSaved }) => {
  const api = useApi();
  const navigate = useNavigate();
  const [vendedores, setVendedores] = useState<Vendedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state (shared for create + edit)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  // Delete confirmation
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Sync from Kame
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      const { synced } = await api.syncVendedoresFromKame();
      await load();
      setSyncMessage(synced > 0 ? `${synced} vendedor${synced !== 1 ? 'es' : ''} importado${synced !== 1 ? 's' : ''} desde Kame.` : 'No hay vendedores nuevos en Kame.');
    } catch (e) {
      setSyncMessage('Error al sincronizar con Kame.');
    } finally {
      setSyncing(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getVendedores();
      setVendedores(data);
    } catch (e) {
      setError('No se pudo cargar los vendedores.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setNameInput('');
    setFieldError(null);
    setDialogOpen(true);
  };

  const openEdit = (v: Vendedor) => {
    setEditingId(v.id);
    setNameInput(v.name);
    setFieldError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!nameInput.trim()) { setFieldError('El nombre es requerido.'); return; }
    try {
      setSaving(true);
      setFieldError(null);
      if (editingId !== null) {
        const updated = await api.updateVendedor(editingId, nameInput.trim());
        setVendedores(prev => prev.map(v => v.id === editingId ? updated : v));
      } else {
        const created = await api.createVendedor(nameInput.trim());
        setVendedores(prev => [...prev, created]);
      }
      setDialogOpen(false);
      onSaved?.();
    } catch (e) {
      setFieldError(e instanceof Error ? e.message : 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (id: number) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (deletingId === null) return;
    try {
      setDeleting(true);
      await api.deleteVendedor(deletingId);
      setVendedores(prev => prev.filter(v => v.id !== deletingId));
      setDeleteDialogOpen(false);
    } catch (e) {
      // keep dialog open, show nothing — rare edge case
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-sans font-semibold text-xl text-foreground">Vendedores</h2>
          <p className="font-sans text-sm text-text-subtle mt-1">
            Trae a tus vendedores de Kame en Atiende
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<ArrowsClockwise size={16} weight="bold" className={syncing ? 'animate-spin' : ''} />}
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Sincronizando...' : 'Sincronizar desde Kame'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus size={16} weight="bold" />}
            onClick={openCreate}
          >
            Agregar vendedor
          </Button>
        </div>
      </div>

      {syncMessage && (
        <p className="font-sans text-sm text-text-subtle">{syncMessage}</p>
      )}

      {/* States */}
      {loading && (
        <div className="flex items-center gap-2 text-text-subtle">
          <Loader />
          <span className="font-sans text-sm">Cargando...</span>
        </div>
      )}

      {error && !loading && (
        <p className="font-sans text-sm text-destructive">{error}</p>
      )}

      {!loading && !error && (
        <div className="flex flex-col gap-2">
          {vendedores.map((v) => (
            <div
              key={v.id}
              className="flex items-center justify-between px-4 py-3 rounded-lg border border-border bg-background"
            >
              <p className="font-sans font-medium text-sm text-text">{v.name}</p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => openEdit(v)}
                  className="p-1.5 rounded hover:bg-muted text-text-subtle hover:text-text transition-colors"
                  aria-label="Editar vendedor"
                >
                  <PencilSimple size={15} weight="regular" />
                </button>
                <button
                  type="button"
                  onClick={() => openDelete(v.id)}
                  className="p-1.5 rounded hover:bg-destructive/10 text-text-subtle hover:text-destructive transition-colors"
                  aria-label="Eliminar vendedor"
                >
                  <Trash size={15} weight="regular" />
                </button>
              </div>
            </div>
          ))}

          {vendedores.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 px-4 border border-dashed border-border rounded-lg">
              <p className="font-sans text-sm text-text-subtle text-center">
                No hay vendedores agregados aún.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="p-6">
          <DialogHeader className="mb-4">
            <DialogTitle>{editingId !== null ? 'Editar vendedor' : 'Agregar vendedor'}</DialogTitle>
          </DialogHeader>
          <Input
            type="text"
            placeholder="Nombre del vendedor"
            value={nameInput}
            onChange={(e) => { setNameInput(e.target.value); setFieldError(null); }}
            error={!!fieldError}
            autoFocus
          />
          {fieldError && (
            <p className="font-sans text-sm text-destructive mt-1">{fieldError}</p>
          )}
          <DialogFooter className="mt-6">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? <Loader /> : editingId !== null ? 'Guardar' : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="p-6">
          <DialogHeader className="mb-4">
            <DialogTitle>Eliminar vendedor</DialogTitle>
          </DialogHeader>
          <p className="font-sans text-sm text-text">
            ¿Estás seguro que deseas eliminar a{' '}
            <strong>{vendedores.find(v => v.id === deletingId)?.name}</strong>?
            Esta acción no se puede deshacer.
          </p>
          <DialogFooter className="mt-6">
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader /> : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Internal extraction-audit review page */}
      <div className="pt-4 border-t border-border">
        <Button variant="secondary" size="sm" onClick={() => navigate('/check')}>
          Check
        </Button>
      </div>
    </div>
  );
};
